import { InvoicePaymentStatus } from '../models/invoice-payment-status';
import {EditProps, Invoice, InvoiceProtocolPick, MergeableSibling} from "../models/table-models";
import {MergeInvoicesDialog} from "./MergeInvoicesDialog";
import {useInvoiceRegistryCheck} from './useInvoiceRegistryCheck';
import {useTranslation} from "react-i18next";
import React, {useContext, useEffect, useState} from "react";
import {AuthContext} from "../context/AuthContext";
import {InvoicePayments} from "./InvoicePayments";
import {useForm} from "react-hook-form";
import { Role } from "../utils/Enums/Roles";
import {
    addNumberToInvoice,
    checkMergeableSiblings,
    confirmSaleInvoice,
    saveProtocolPicks,
    getDocumentInvoices,
    mergeAndConfirmInvoices,
    markInvoiceAsPaid,
    MarkAsPaidRequest,
    saveInvoicesToDocument,
    useGetDocument
} from "../utils/clients-hooks";
import {toast} from "react-toastify";
import {CustomToastContent} from "./CustomToastContent";
import {toastOptionsTopRight} from "../utils/Enums/ToastOptions";
import {showBackendError} from "../utils/backend-error";
import {Button, Form, Modal} from "react-bootstrap";
import {invoicesToDocumentTableHeaders} from "../utils/Enums/TableHeaders";
import {toSofiaLocalISOString, transformDateToString, transformPriceToString} from "../utils/helper-functions";
import {EditInvoiceWrapper} from "../pages/Invoices";
import {CustomTable} from "./CustomTable";
import {DocumentProtocols} from "./DocumentProtocols";
import {CreditNoteOffsetPanel} from "./CreditNoteOffsetPanel";
import {
    invoicePaymentOptions,
    invoicePaymentTypes,
    invoiceStatus,
} from "../utils/Enums/InvoiceFields";
import CommonInputText from "./CommonInputText";
import ReactDatePicker from "react-datepicker";
import { DocumentView } from "../models/dashboard-models";
import { resourceCache, useAsyncResource } from "use-async-resource";
import { useGetAllTurnovers } from "../utils/users-hooks";

export const AddInvoicesToDocument = ({
    invoices,
    document,
    setDocument,
    setShowModal,
    refetchEntitiesHook,
    onSubmit,
    onInvoiceClosed,
}: {
    /** Последното плащане затвори фактурата - подава се нагоре, за да се затвори диалогът над нея. */
    onInvoiceClosed?: () => void;

    setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
    invoices: Invoice[];
    document?: DocumentView;
    setDocument?: React.Dispatch<React.SetStateAction<DocumentView | undefined>>;
    refetchEntitiesHook: () => void;
    onSubmit: (document: DocumentView) => void;
}) => {
    const { t } = useTranslation('translation', { keyPrefix: 'invoice' });
    const { t: newRequestT } = useTranslation('translation', { keyPrefix: 'newRequest' });
    const { hasPermission } = useContext(AuthContext);
    // Видът се взема от вече съществуващия документ (или от задълженията), а не се заковава на
    // фактура: `saveInvoicesToDocument` разпространява избраното върху документа И върху всяко
    // задължение, тоест твърдото 'INVOICE' пренаписваше стоковата разписка на обект, който
    // нарочно е на разписка, при всяко отваряне на диалога.
    const initialDocumentType =
        document?.documentType ??
        invoices[0]?.document?.documentType ??
        invoices[0]?.documentType ??
        'INVOICE';

    const { register, handleSubmit, getValues, setValue } = useForm<DocumentView>({
        mode: 'all',
        defaultValues: {
            ...document,
            isPaid: document?.isPaid ?? InvoicePaymentStatus.Unpaid,
            paymentType: document?.paymentType?.toLowerCase(),
            documentType: initialDocumentType,
            invoices: invoices.map(({id}) => id).filter((id): id is number => id !== undefined),
            total: (invoices.reduce((acc, curr) => acc + curr.total, 0)) / 100
        },
    });

    const [oneDocument, setOneDocument] = useState(false);

    useEffect(() => {
        setValue('isPaid', document?.paid ? InvoicePaymentStatus.Paid : (document?.isPaid ?? InvoicePaymentStatus.Unpaid));
    }, [document?.paid, document?.isPaid, setValue]);

    useEffect(() => {
        setDocument?.({
            ...getValues(),
        })
    }, []);

    useEffect(() => {
        // Редовете на документа пристигат асинхронно след инициализацията на формата.
        setValue('invoices', invoices.map(({ id }) => id).filter((id): id is number => id !== undefined));
        setValue('total', invoices.reduce((acc, curr) => acc + curr.total, 0) / 100);
        if (invoices.length > 0) {
            const differentDocument = invoices.some((item) => item.document?.id !== invoices[0].document?.id);
            setOneDocument(() => !differentDocument);
        } else
            setOneDocument(() => false);
    }, [invoices, setValue]);

    const isFinancial = hasPermission([Role.FINANCIAL]);
    const isCoordinator = hasPermission([Role.COORDINATOR]);
    // Кредитирана и неплатена: затваря се с „Прихвани с КИ“, не с „Платено“ или плащане.
    const creditedUnpaid = document?.creditNoteNumber != null && !document?.paid;

    const [showMergeDialog, setShowMergeDialog] = useState(false);
    const [mergeableSiblings, setMergeableSiblings] = useState<MergeableSibling[]>([]);
    const [isConfirming, setIsConfirming] = useState(false);
    const { checkBeforeIssue, registryDialog, registryBusy } = useInvoiceRegistryCheck();

    const addNumber = async (doc?: DocumentView) => {
        if (!invoices[0]) return;
        await checkBeforeIssue({ ...invoices[0], documentType: doc?.documentType || invoices[0].documentType },
            () => continueNumbering(doc));
    };
    const continueNumbering = async (doc?: DocumentView) => {
        // Продажбата държи документа си едно към едно, тоест doc.invoices е празен и бутонът дотук просто
        // не правеше нищо. Номерирането ѝ минава по свой път и без сливане: няма сестрински фактури, с
        // които да се обедини, а mergeableSiblings търси id-то в таблицата на наемите.
        const sale = invoices[0]?.isSaleInvoice ? invoices[0] : undefined;
        if (sale?.id) {
            setIsConfirming(true);
            try {
                await confirmSaleInvoice(sale.id);
                resourceCache(useGetDocument).clear();
                refetchEntitiesHook?.();
                toast.success(<CustomToastContent content={[t('DocumentComfirm')]}/>, toastOptionsTopRight);
            } catch (error) {
                showBackendError(error);
            } finally {
                setIsConfirming(false);
            }
            return;
        }

        if (!doc?.invoices[0]) return;
        setIsConfirming(true);
        try {
            const siblings = await checkMergeableSiblings(doc.invoices[0]);
            if (invoices[0]?.documentType === 'invoice' || (siblings && siblings.length > 0)) {
                setMergeableSiblings(siblings || []);
                setShowMergeDialog(true);
            } else {
                await confirmSingle(doc);
            }
        } finally {
            setIsConfirming(false);
        }
    };

    const confirmSingle = async (doc?: DocumentView, corrections?: import('../models/invoice-corrections').InvoiceCorrectionInput[], breakdowns?: import('../models/invoice-corrections').InvoiceBreakdownInput[]) => {
        if ((corrections !== undefined || breakdowns?.length) && doc?.invoices[0]) await mergeAndConfirmInvoices([doc.invoices[0]], corrections, breakdowns);
        else await addNumberToInvoice(doc?.invoices[0], doc?.comment ? doc?.comment : "");
        resourceCache(useGetDocument).clear();
        refetchEntitiesHook?.();
        toast.success(<CustomToastContent content={[t('DocumentComfirm')]}/>, toastOptionsTopRight);
    };

    const confirmOnlyThis = async (picks: InvoiceProtocolPick[], corrections?: import('../models/invoice-corrections').InvoiceCorrectionInput[], breakdowns?: import('../models/invoice-corrections').InvoiceBreakdownInput[]) => {
        if (picks.length > 0) {
            await saveProtocolPicks(picks);
        }
        await confirmSingle(document, corrections, breakdowns);
        setShowMergeDialog(false);
    };

    const handleMergeSelected = async (invoicingIds: number[], picks: InvoiceProtocolPick[], corrections?: import('../models/invoice-corrections').InvoiceCorrectionInput[], breakdowns?: import('../models/invoice-corrections').InvoiceBreakdownInput[]) => {
        setIsConfirming(true);
        try {

            // Запазваме избора преди издаването, за да няма издадена фактура при неуспешен запис на протоколите.
            if (picks.length > 0) {
                await saveProtocolPicks(picks);
            }

            await mergeAndConfirmInvoices(invoicingIds, corrections, breakdowns);
            setShowMergeDialog(false);

            resourceCache(useGetDocument).clear();
            refetchEntitiesHook?.();
            toast.success(<CustomToastContent content={[t('DocumentComfirm')]}/>, toastOptionsTopRight);
        } finally {
            setIsConfirming(false);
        }
    };

    return (
        <div className={''}>
            <div>
                <CustomTable
                    columns={{
                        headings: invoicesToDocumentTableHeaders,
                        items: invoices.map(
                            ({
                                branchName,
                                fromDate,
                                duePaymentDate,
                                creationDate,
                                documentType,
                                user,
                                paymentMethod,
                                total,
                                document,
                            }) => [
                                document?.invoiceNumber,
                                transformDateToString(creationDate),
                                t(documentType),
                                branchName,
                                transformDateToString(fromDate),
                                transformDateToString(duePaymentDate),
                                user,
                                transformPriceToString(total),
                                t(paymentMethod),
                            ]
                        ),
                    }}
                    tableData={invoices}
                    refetchEntitiesHook={refetchEntitiesHook}
                />
            </div>
            <Form
                id='form'
                className='d-flex flex-column align-items-center p-4  w-100'
                noValidate
                onSubmit={handleSubmit((formValue) => {
                    onSubmit(formValue);
                })}
            >
                <div className={'d-flex w-100 justify-content-end'}>
                    <div className={'d-flex  mt-3'}>
                        {oneDocument &&
                            document?.invoiceNumber == null &&
                            document?.paymentType.toLowerCase() == 'bank' && (
                                <Button
                                    disabled={isConfirming || registryBusy}
                                    className={'btn-success'}
                                    onClick={() => addNumber(document)}
                                >
                                    {t('confirm')}
                                </Button>
                            )}
                    </div>
                    <Form.Group controlId='total'>
                        <CommonInputText
                            id='total'
                            label={t('total') + '  (' + invoices[0].currency + ')'}
                            labelTextColor='text-black'
                            type='number'
                            disabled={true}
                            hookRegister={register('total', {
                                required: {
                                    value: true,
                                    message: t('total'),
                                },
                                min: {
                                    value: 1,
                                    message: t('total'),
                                },
                            })}
                        />
                    </Form.Group>
                </div>
                <div className='w-100 d-flex mb-3 pb-3'>
                    <div className='w-50 d-inline-block m-0 p-1'>
                        <label className='mb-2'>{t('paid type')}</label>
                        <Form.Select {...register('paymentType')}>
                            {invoicePaymentOptions.map((item, index) => (
                                <option
                                    key={item.value}
                                    value={item.value}
                                    disabled={
                                        (document?.paid && document.paid && (!isCoordinator || !isFinancial)) ||
                                        document?.invoiceNumber != null
                                    }
                                >
                                    {newRequestT(item.name)}
                                </option>
                            ))}
                        </Form.Select>
                    </div>
                    {(isFinancial || isCoordinator) && (
                        <div className='w-50 d-inline-block m-0 p-1'>
                            <label className='mb-2'>{t('documentType')}</label>
                            <Form.Select defaultValue={initialDocumentType} {...register('documentType')}>
                                {invoicePaymentTypes.map((option, index) => (
                                    <option
                                        key={option.value}
                                        value={option.value}
                                        disabled={
                                            (document?.paid && document.paid && (!isCoordinator || !isFinancial)) ||
                                            document?.invoiceNumber != null
                                        }
                                    >
                                        {option.name && t(option.name)}
                                    </option>
                                ))}
                            </Form.Select>
                        </div>
                    )}
                    {(isFinancial || isCoordinator) && (
                        <div className='w-50 d-inline-block m-0 p-1'>
                            <label className='mb-2'>{t('state')}</label>
                            <Form.Select {...register('isPaid')}>
                                {invoiceStatus.map((option, index) => (
                                    <option
                                        key={option.value}
                                        value={option.value}
                                        disabled={document?.invoiceNumber == null || (document?.paid && document.paid) || creditedUnpaid}
                                    >
                                        {option.name && t(option.name)}
                                    </option>
                                ))}
                            </Form.Select>
                        </div>
                    )}
                </div>
            </Form>

            {invoices[0] &&
                !invoices[0].isSaleInvoice &&
                invoices[0].paymentMethod === 'bank' &&
                document?.invoiceNumber != null &&
                // Кредитирана: само ако има въведени плащания, и то за преглед.
                (!creditedUnpaid || (invoices[0].document?.paidAmount ?? 0) > 0) &&
                !invoices[0].creditNoteOffset &&
                (isFinancial || isCoordinator) && (
                    <InvoicePayments
                        invoice={invoices[0]}
                        readOnly={creditedUnpaid}
                        refetch={refetchEntitiesHook}
                        onInvoiceClosed={() => {
                            setValue('isPaid', InvoicePaymentStatus.Paid);
                            if (onInvoiceClosed) onInvoiceClosed();
                            else setShowModal(false);
                        }}
                    />
                )}

            {registryDialog}
            <MergeInvoicesDialog
                show={showMergeDialog}
                onHide={() => setShowMergeDialog(false)}
                currentInvoice={invoices[0]}
                siblings={mergeableSiblings}
                onConfirmOnlyThis={confirmOnlyThis}
                onMergeSelected={handleMergeSelected}
            />
        </div>
    );
};




export const EditDocument = ({ itemToEdit, setShowModal, refetchEntitiesHook, setIsLoading, invoices, onInvoiceClosed }:
    EditProps<Invoice> & {
        invoices: Invoice[];
        /** Подава се нагоре, когато последното плащане затвори фактурата. */
        onInvoiceClosed?: () => void;
    }
) => {
    const { t } = useTranslation('translation', { keyPrefix: 'newRequest' });
    const { t: invoiceT } = useTranslation('translation', { keyPrefix: 'invoice' });
    const { hasPermission } = useContext(AuthContext);

    const [readDocument, fetchNewDocument] = useAsyncResource(useGetDocument, itemToEdit?.document?.id);

    // Всички редове на документа, изтеглени тук: подадените отвън зависят от това какво е показано на
    // страницата, тоест от клик върху реда идваше само той, а от молива - цялата страница.
    const [documentInvoices, setDocumentInvoices] = useState<Invoice[]>([]);
    const [showMarkAsPaidForm, setShowMarkAsPaidForm] = useState(false);
    const [showMarkAsPaidConfirm, setShowMarkAsPaidConfirm] = useState(false);
    const [markAsPaidLoading, setMarkAsPaidLoading] = useState(false);
    // Номерата се попълват от документа, когато ги има. Продажбата по банка се номерира от офиса още при
    // потвърждението, така че при плащането операторът няма какво да преписва - а полето беше празно и
    // задължително, тоест плащането не тръгваше без ръчно въведен номер.
    const [invoiceNumber, setInvoiceNumber] = useState<string>(
        itemToEdit?.document?.invoiceNumber != null ? String(itemToEdit.document.invoiceNumber) : '');
    const [documentNumber, setDocumentNumber] = useState<string>(
        itemToEdit?.document?.documentNumber != null ? String(itemToEdit.document.documentNumber) : '');
    const [paymentTimestamp, setPaymentTimestamp] = useState<Date | null>(null);

    const document = readDocument();

    useEffect(() => {
        const documentId = itemToEdit?.document?.id;
        if (!documentId) return;

        let current = true;
        getDocumentInvoices(documentId)
            .then((rows) => current && setDocumentInvoices(rows))
            // Останалите редове са удобство: без тях се показват подадените отвън, както досега.
            .catch(() => current && setDocumentInvoices([]));

        return () => {
            current = false;
        };
    }, [itemToEdit?.document?.id]);

    const isAdmin = hasPermission([Role.ADMIN, Role.COORDINATOR]);
    // Кредитираната фактура не се маркира платена - за нея е „Прихвани с КИ“ отдолу.
    const isUnpaid = itemToEdit && !itemToEdit.paid && itemToEdit.document?.creditNoteNumber == null;
    const isSaleInvoice = itemToEdit?.isSaleInvoice ?? false;

    const save = async (document?: DocumentView) => {
        await saveInvoicesToDocument(document);
        resourceCache(useGetDocument).clear();
        fetchNewDocument(itemToEdit?.document?.id);

        if (document?.isPaid === InvoicePaymentStatus.Paid) {
            setShowModal(false)
            resourceCache(useGetAllTurnovers).clear();
        }

        refetchEntitiesHook?.();

        toast.success(<CustomToastContent content={[t('DocumentComfirm')]} />, toastOptionsTopRight);
    };

    const handleMarkAsPaid = async () => {
        if (!itemToEdit?.id || !invoiceNumber) {
            toast.error(<CustomToastContent content={[invoiceT('invoiceNumberRequired')]} />, toastOptionsTopRight);
            return;
        }

        setMarkAsPaidLoading(true);
        try {
            const request: MarkAsPaidRequest = {
                id: itemToEdit.id,
                invoiceNumber: Number.parseInt(invoiceNumber, 10),
                documentNumber: documentNumber ? Number.parseInt(documentNumber, 10) : undefined,
                paymentTimestamp: paymentTimestamp ? toSofiaLocalISOString(paymentTimestamp) : undefined,
                isSale: isSaleInvoice,
            };

            await markInvoiceAsPaid(request);
            toast.success(<CustomToastContent content={[invoiceT('markedAsPaid')]} />, toastOptionsTopRight);
            resourceCache(useGetAllTurnovers).clear();
            setShowModal(false);
            refetchEntitiesHook?.();
        } catch (error) {
            toast.error(<CustomToastContent content={[invoiceT('errorMarkingAsPaid')]} />, toastOptionsTopRight);
        } finally {
            setMarkAsPaidLoading(false);
        }
    };

    const fallbackProtocolInvoices = itemToEdit ? [itemToEdit] : [];

    return (
        <>
            {/* Admin Mark as Paid Section */}
            {isAdmin && isUnpaid && (
                <div className='mb-3 p-3 border rounded bg-light'>
                    <div className='d-flex justify-content-between align-items-center mb-2'>
                        <strong>{invoiceT('devMarkAsPaid')}</strong>
                        <Button
                            variant='outline-primary'
                            size='sm'
                            onClick={() => setShowMarkAsPaidForm(!showMarkAsPaidForm)}
                        >
                            {showMarkAsPaidForm ? invoiceT('hide') : invoiceT('show')}
                        </Button>
                    </div>
                    {showMarkAsPaidForm && (
                        <div className='mt-2'>
                            <Form.Group className='mb-2'>
                                <Form.Label>{invoiceT('sequenceInvoiceNumber')} *</Form.Label>
                                <Form.Control
                                    type='number'
                                    value={invoiceNumber}
                                    min={1}
                                    onChange={(e) => setInvoiceNumber(e.target.value)}
                                    placeholder={invoiceT('enterInvoiceNumber')}
                                />
                            </Form.Group>
                            <Form.Group className='mb-2'>
                                <Form.Label>{invoiceT('documentNumber')}</Form.Label>
                                <Form.Control
                                    type='number'
                                    value={documentNumber}
                                    min={1}
                                    onChange={(e) => setDocumentNumber(e.target.value)}
                                    placeholder={invoiceT('enterDocumentNumber')}
                                />
                            </Form.Group>
                            <Form.Group className='mb-2'>
                                <Form.Label>{invoiceT('paymentTimestamp')}</Form.Label>
                                <ReactDatePicker
                                    selected={paymentTimestamp}
                                    onChange={(date) => setPaymentTimestamp(date)}
                                    showTimeSelect
                                    timeIntervals={15}
                                    timeInputLabel='Time:'
                                    dateFormat='dd/MM/yyyy HH:mm'
                                    timeFormat='HH:mm'
                                    maxDate={new Date()}
                                    filterTime={(time) => time <= new Date()}
                                    isClearable
                                    placeholderText='dd/MM/yyyy HH:mm'
                                    className='border border-secondary text-start p-2 py-1 rounded w-100'
                                    onChangeRaw={(e) => e.preventDefault()}
                                    popperPlacement='bottom-end'
                                />
                            </Form.Group>
                            <Button
                                variant='success'
                                onClick={() => setShowMarkAsPaidConfirm(true)}
                                disabled={markAsPaidLoading || !invoiceNumber}
                            >
                                {markAsPaidLoading ? invoiceT('loading') : invoiceT('markAsPaid')}
                            </Button>
                        </div>
                    )}
                </div>
            )}

            <Modal show={showMarkAsPaidConfirm} onHide={() => setShowMarkAsPaidConfirm(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{invoiceT('markAsPaidConfirmTitle')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>{invoiceT('markAsPaidConfirmWarning')}</p>
                    <p className='text-danger fw-bold'>{invoiceT('markAsPaidIrreversible')}</p>
                </Modal.Body>
                <Modal.Footer style={{ justifyContent: 'space-between' }}>
                    <Button variant='secondary' onClick={() => setShowMarkAsPaidConfirm(false)}>
                        {invoiceT('cancel')}
                    </Button>
                    <Button
                        variant='danger'
                        onClick={() => {
                            setShowMarkAsPaidConfirm(false);
                            handleMarkAsPaid();
                        }}
                    >
                        {invoiceT('confirmMarkAsPaid')}
                    </Button>
                </Modal.Footer>
            </Modal>

            <CreditNoteOffsetPanel
                invoice={itemToEdit}
                onDone={() => {
                    resourceCache(useGetAllTurnovers).clear();
                    setShowModal(false);
                    refetchEntitiesHook?.();
                }}
            />

            {/* Original content */}
            {document?.invoices.length > 1 ? (
                <AddInvoicesToDocument
                    setShowModal={setShowModal}
                    {...{ onInvoiceClosed }}
                    invoices={
                        documentInvoices.length > 0
                            ? documentInvoices
                            : invoices.filter((value) => document.invoices.includes(value.id as number))
                    }
                    document={document}
                    refetchEntitiesHook={refetchEntitiesHook}
                    onSubmit={save}
                />
            ) : (
                <EditInvoiceWrapper
                    itemToEdit={itemToEdit}
                    setShowModal={setShowModal}
                    setIsLoading={setIsLoading}
                    refetchEntitiesHook={refetchEntitiesHook}
                />
            )}

            {/* Протоколите на документа: какво ще тръгне с мейла, с възможност да се смени. */}
            <DocumentProtocols invoices={documentInvoices.length > 0 ? documentInvoices : fallbackProtocolInvoices} />
        </>
    );
}
